import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  Grid,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useGetApi } from "../hooks/useGetApi";
import { getPatientByIdApi } from "../services/patient.service";
import { getUserApi } from "../services/user.service";
import DiscountModal from "../components/consultation/modal/discount-modal";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { toast } from "react-toastify";
import { createConsultationServicesApi } from "../services/consultation.service";
import { getCategoryApi } from "../services/category.service";
import BulkWaiverModal from "../components/consultation/modal/bulk-waiver-modal";
import ServiceRow from "../components/consultation/ServiceRow";

const ConsultationAddService = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [patientData, setPatientData] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isErrorPatient, setIsErrorPatient] = useState(false);

  useEffect(() => {
    async function fetchPatient() {
      if (patientId && patientId !== 'undefined' && patientId !== 'null') {
        setIsLoadingPatient(true);
        setIsErrorPatient(false);
        try {
          const response = await getPatientByIdApi({ patientId });
          if (response?.code === 200 && response?.data) {
            setPatientData(response.data);
          } else {
            setPatientData(null);
            setIsErrorPatient(true);
          }
        } catch (err) {
          setPatientData(null);
          setIsErrorPatient(true);
        } finally {
          setIsLoadingPatient(false);
        }
      }
    }
    fetchPatient();
  }, [patientId]);

  console.log('patientId from useParams', patientId);
  console.log('patientData (fetched):', patientData);
  console.log('isLoadingPatient:', isLoadingPatient, 'isErrorPatient:', isErrorPatient);

  const initialState = {
    UHID: "",
    patientId,
    discount: {
      discountReason: "",
      approvedBy: null,
      discountType: "Percentage",
      discountAmount: 0,
      discountPercentage: 0,
    },
    payment: {
      transactionType: null,
      transactionId: "",
      amountPaid: 0,
      remarks: "To Reception",
      paymentType: "",
    },
    services: [
      {
        id: "1",
        category: null,
        item: null,
        quantity: 1,
        assignedDoctor: null,
        amount: 0,
        referringDoctorName: "",
        waiver: {
          waiverReason: "",
          approvedBy: "",
          waiverType: "Percentage",
          waiverAmount: 0,
          waiverPercentage: 0,
        },
      },
    ],
  };

  // Determine the highest id from initial services and set the next ID
  const initialMaxId = Math.max(
    ...initialState.services?.map((s) => parseInt(s?.id, 10)),
    0
  );
  const nextIdRef = useRef(initialMaxId + 1);

  const [formData, setFormData] = useState(initialState);
  const [confirmAmountPaid, setConfirmAmountPaid] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkWaiverModalOpen, setBulkWaiverModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isTransactionTypeSet, setIsTransactionTypeSet] = useState(false);

  // Updated validation logic to allow partial payments for "Pending Payment" transaction type
  const isPendingPayment = formData?.payment?.transactionType?.transactionMode === "Pending Payment";
  const amountsMatch = isPendingPayment 
    ? (Number(confirmAmountPaid) >= 0 && Number(confirmAmountPaid) <= Number(formData?.payment?.amountPaid))
    : formData?.payment?.amountPaid === Number(confirmAmountPaid);

  const netExpense = formData?.services?.reduce((total, service) => {
    const normalRate = service?.amount
      ? service.amount
      : service?.item?.normalRate ?? 0;
    return total + normalRate;
  }, 0);

  const amountToBePaid = formData?.services?.reduce((total, service) => {
    const normalRate = service?.amount
      ? service.amount
      : service?.item?.normalRate ?? 0;
    const waiverPercentage = service?.waiver?.waiverPercentage ?? 0;
    const waiverAmount =
      service?.waiver?.waiverType === "Percentage"
        ? (normalRate * waiverPercentage) / 100
        : service?.waiver?.waiverAmount ?? 0;

    return total + normalRate - waiverAmount;
  }, 0);
  // Apply discount if discountAmount or discountPercentage is greater than 0
  const finalAmountToBePaid =
    formData?.discount?.discountAmount > 0 ||
    formData?.discount?.discountPercentage > 0
      ? netExpense -
        (formData?.discount?.discountType === "Percentage"
          ? (netExpense * (formData?.discount?.discountPercentage ?? 0)) / 100
          : formData?.discount?.discountAmount ?? 0)
      : amountToBePaid;

  const formattedAmountToBePaid = finalAmountToBePaid.toFixed(2);

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "category" },
    dataKey: "categories",
  });

  const { dataList: transactionList } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });

  const transactionTypeList = [
    ...(transactionList?.find((option) => option?.transactionMode === "Payment")?.transactionTypes || []),
    ...(transactionList?.find((option) => option?.transactionMode === "Pending Payment")?.transactionTypes || [])
  ];

  const handleModalOpen = () => {
    // Check if any service in the formData has a waiver amount or percentage greater than 0
    const hasWaiver = formData?.services?.some(
      (service) =>
        service?.waiver?.waiverAmount > 0 ||
        service?.waiver?.waiverPercentage > 0
    );

    // If there is a waiver present, show an error message and exit the function
    if (hasWaiver) {
      toast.error("Please remove the waiver before adding overall discount.");
      return;
    }

    // If no waiver is present, proceed to open the modal
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleBulkWaiverModalOpen = () => {
    const hasDiscount =
      formData?.discount?.discountAmount > 0 ||
      formData?.discount?.discountPercentage > 0;

    if (hasDiscount) {
      toast.error(
        "Please remove the overall discount before adding bulk waiver."
      );
      return;
    }
    setBulkWaiverModalOpen(true);
  };

  const handleBulkWaiverModalClose = () => {
    setBulkWaiverModalOpen(false);
  };

  const toggleRowSelection = (serviceId) => {
    setSelectedServices((prevSelected) => {
      if (prevSelected.includes(serviceId)) {
        return prevSelected.filter((id) => id !== serviceId); // Deselect
      } else {
        return [...prevSelected, serviceId]; // Select
      }
    });
  };
  const handleInputChange = (e, key) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    if (key) {
      setFormData((preValue) => ({
        ...preValue,
        [key]: {
          ...preValue?.[key],
          [name]: parsedValue,
          ...(name === "ward" && { bed: null }),
        },
      }));
      return;
    }

    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleServicesChange = (e, index, key) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    if (key) {
      const hasDiscount =
        formData?.discount?.discountAmount > 0 ||
        formData?.discount?.discountPercentage > 0;
      if (hasDiscount) {
        toast.error("Please remove the overall discount before adding waiver.");
        return;
      }
    }

    setFormData((prev) => {
      const updatedServicesDetails = prev?.services?.map((service, i) => {
        if (i === index) {
          if (key) {
            const updatedWaiver = { ...service?.waiver, [name]: parsedValue };

            // Conditional logic for waiverAmount and waiverPercentage
            if (name === "waiverType") {
              if (parsedValue === "Percentage" && updatedWaiver?.waiverAmount) {
                updatedWaiver.waiverAmount = 0;
              } else if (
                parsedValue === "Amount" &&
                updatedWaiver?.waiverPercentage
              ) {
                updatedWaiver.waiverPercentage = 0;
              }
            }

            return { ...service, waiver: updatedWaiver };
          }
          return {
            ...service,
            [name]: parsedValue,
            ...(name === "category" && { item: null }),
            ...(name === "item" && {
              amount: Number(parsedValue?.normalRate) || 0,
            }),
          };
        }
        return service;
      });

      return { ...prev, services: updatedServicesDetails };
    });
  };

  const addServicesDetail = () => {
    const newId = nextIdRef.current.toString(); // Generate new id as a string
    nextIdRef.current += 1; // Increment for the next service

    const newService = {
      id: newId,
      category: null,
      item: null,
      quantity: 1,
      assignedDoctor: "",
      amount: 0,
      referringDoctorName: "",
      waiver: {
        waiverReason: "",
        approvedBy: "",
        waiverType: "Percentage",
        waiverAmount: 0,
        waiverPercentage: 0,
      },
    };

    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  const removeServicesDetail = (serviceId) => {
    console.log("btn pressedß");
    setFormData((prev) => ({
      ...prev,
      services: prev?.services?.filter((s) => s?.id !== serviceId),
    }));
    setSelectedServices((prevSelected) =>
      prevSelected.filter((id) => id !== serviceId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData?.payment?.transactionType &&
      formattedAmountToBePaid !== formData?.payment?.amountPaid?.toFixed(2)
    ) {
      toast.error("Amount and Amount to be paid doesn't match.");
      return;
    }

    if (!amountsMatch) {
      toast.error("Amount and Confirm Amount doesn't match.");
      return;
    }

    const formDataWithId = {
      ...formData,
      payment: {
        ...formData?.payment,
        transactionType:
          typeof formData?.payment?.transactionType === "object"
            ? formData?.payment?.transactionType?._id
            : formData?.payment?.transactionType,
      },
      services: formData?.services?.map((service) => ({
        ...service,
        category:
          typeof service?.category === "object"
            ? service?.category?._id
            : service?.category,
        item:
          typeof service?.item === "object"
            ? service?.item?._id
            : service?.item,
        assignedDoctor:
          typeof service?.assignedDoctor === "object"
            ? service?.assignedDoctor?._id
            : service?.assignedDoctor,
      })),
    };

    setIsLoading(true);
    const response = await createConsultationServicesApi(formDataWithId);
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(response?.message || `Appointment added successfully`);
      navigate("/patient");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    setFormData((preValue) => ({ ...preValue, UHID: patientData?.UHID }));
  }, [patientData]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {isLoadingPatient ? (
          <Loader />
        ) : isErrorPatient ? (
          <ErrorMessage />
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              padding: 2,
              borderRadius: 2,
              marginRight: "90px",
              marginTop: 1,
              width: "100%",
            }}
          >
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#5D6A82', 
                textTransform: 'none', 
                fontSize: '16px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'none !important',
                marginBottom: '16px',
              }}
            >
              Back
            </Button>
            {/* Consultation Form */}
            <Typography
              sx={{
                fontFamily: 'Open Sans',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '29px',
                letterSpacing: '0px',
                color: "#5D6A82",
                marginBottom: "1rem",
              }}
            >
              Enter Service Details
            </Typography>

              {/* Patient Information */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4} md={3} xl={2}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid #5d6a82",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                      boxSizing: "border-box",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {patientData?.patientImage ? (
                      <Box
                        component="img"
                        src={patientData.patientImage}
                        alt="Patient"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          fontSize: "14px",
                          textAlign: "center",
                        }}
                      >
                        No picture
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8} md={9} xl={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="grey">
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="UHID"
                        name="UHID"
                        value={patientData?.UHID || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Name"
                        name="name"
                        value={`${patientData?.firstName} ${patientData?.lastName}`}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Number"
                        name="phone"
                        value={patientData?.phone || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Gender"
                        name="gender"
                        value={patientData?.gender || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Address"
                        name="address"
                        value={patientData?.address || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="City"
                        name="city"
                        value={patientData?.city?.cityName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="State"
                        name="state"
                        value={patientData?.state?.stateName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Pincode"
                        name="pinCode"
                        value={patientData?.pinCode || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Services */}
              <Box
                sx={{
                  py: 3,
                  px: 2,
                  bgcolor: "#f5faff",
                  borderRadius: 2,
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Services
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      sx={{ mr: 1, textTransform: "none" }}
                    >
                      Relief
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                      disabled={selectedServices?.filter(Boolean).length < 2}
                      onClick={handleBulkWaiverModalOpen}
                    >
                      Bulk Waiver
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      onClick={addServicesDetail}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                <Box>
                  {formData?.services?.map((service, index) => (
                    <ServiceRow
                      key={index}
                      service={service}
                      index={index}
                      handleServicesChange={handleServicesChange}
                      categoryList={categoryList}
                      doctorsList={doctorsList}
                      formData={formData}
                      removeServicesDetail={removeServicesDetail}
                      selectedServices={selectedServices}
                      toggleRowSelection={toggleRowSelection}
                      consultationType="IPD"
                    />
                  ))}

                  <Typography sx={{ textAlign: "right", fontWeight: "bold" }}>
                    Amount: ₹{formattedAmountToBePaid || 0}
                  </Typography>
                </Box>
              </Box>

              {/* Billing Information */}
              {/* {consultationType !== "IPD" && ( */}
              <Box
                sx={{
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                {/* Overall Discount */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                    onClick={handleModalOpen}
                  >
                    Overall Discount
                  </Button>

                  <DiscountModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    handleChange={handleInputChange}
                    formData={formData}
                    setFormData={setFormData}
                  />
                  <BulkWaiverModal
                    open={bulkWaiverModalOpen}
                    onClose={handleBulkWaiverModalClose}
                    handleServicesChange={handleServicesChange}
                    formData={formData}
                    setFormData={setFormData}
                    selectedServices={selectedServices}
                    setSelectedServices={setSelectedServices}
                  />
                </Box>

                {/* Amount To Be Paid */}
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#EAEAEA",
                    borderRadius: 2,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Amount to be paid: ₹{formattedAmountToBePaid}
                    {/* {amount -
                      (formData?.discount?.discountType === "Percentage"
                        ? formData?.discount?.discountPercentage * amount * 0.01
                        : formData?.discount?.discountAmount)} */}
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>|</Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Net Expense: ₹{netExpense}
                  </Typography>
                </Box>

                {/* Transaction Details */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={transactionTypeList || []}
                      getOptionLabel={(option) =>
                        `${option?.name || ""} (${option?.paymentMethod})`
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Transaction Type" 
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                        />
                      )}
                      value={formData?.payment?.transactionType || null}
                      onChange={(_, newValue) => {
                        setIsTransactionTypeSet(!!newValue);
                        handleInputChange(
                          {
                            target: {
                              name: "transactionType",
                              value: newValue,
                            },
                          },
                          "payment"
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                      sx={{ minWidth: "100px" }}
                      required
                      fullWidth
                    >
                      <InputLabel 
                        id="payment-type-id"
                        required
                        sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                      >
                        Payment Type
                      </InputLabel>
                      <Select
                        labelId="payment-type-id"
                        label="Payment Type"
                        name="paymentType"
                        value={formData?.payment?.paymentType || ""}
                        onChange={(e) => handleInputChange(e, "payment")}
                      >
                        <MenuItem value="InvoicePayment">
                          Invoice Payment
                        </MenuItem>
                        <MenuItem value="SelfPayment">Self Payment</MenuItem>
                        <MenuItem value="Refund">Refund</MenuItem>
                        <MenuItem value="IPDAdvance">IPD Advance</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {formData?.payment?.transactionType?.name === "QR Payment" ? (
                      <TextField
                        label="Transaction ID"
                        name="transactionId"
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        value={formData?.payment?.transactionId || ""}
                        onChange={(e) => handleInputChange(e, "payment")}
                        fullWidth
                        sx={{ bgcolor: "white" }}
                      />
                    ) : (
                      <TextField
                        label="Transfer Bank A/C"
                        name="transferBankAccount"
                        value={
                          formData?.payment?.transactionType?.bankDetails?.find(
                            (bankDetail) => bankDetail?.default
                          )?.accountNumber || ""
                        }
                        slotProps={{ input: { readOnly: true } }}
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        fullWidth
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Amount (₹)"
                      name="amountPaid"
                      value={formData?.payment?.amountPaid || ""}
                      onChange={(e) => handleInputChange(e, "payment")}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      type="number"
                      label="Confirm Amount (₹)"
                      name="confirmAmountPaid"
                      value={confirmAmountPaid || ""}
                      onChange={(e) =>
                        setConfirmAmountPaid(Number(e.target.value) || 0)
                      }
                      fullWidth
                      error={!amountsMatch && confirmAmountPaid !== ""}
                      helperText={
                        !amountsMatch && confirmAmountPaid !== ""
                          ? isPendingPayment 
                            ? `Amount must be between ₹0 and ₹${formData?.payment?.amountPaid}`
                            : "Amount does not match!"
                          : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Remarks"
                      name="remarks"
                      value={formData?.payment?.remarks || ""}
                      onChange={(e) => handleInputChange(e, "payment")}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* )} */}

              {/* Register Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : `Submit`}
                </Button>
              </Box>
            </Box>
          )}
      </Box>
    </Container>
  );
};

export default ConsultationAddService;
